import { ThumbDownOutlined, ThumbUpOutlined } from "@mui/icons-material";
import { Box, Button, Tooltip } from "@mui/material";
import debounce from "lodash.debounce";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CandidateReviewHelpfulnessType } from "../../../../../App/Enums/CandidateEnums";
import { useSaveReviewHelpfulnessMutation } from "../../../../../App/Queries/ReviewQueries";
import { useNotification } from "../../../../../App/State/Hooks/notificationHooks";
import { ICandidateReview } from "../../../../../App/Typings/Candidate";
import { CandidateContext } from "../../../../CandidateProfile";
import { useCurrentUser } from "../../../../../App/Queries/UserQueries";
import { AppFeatureLookup, defaultUnauthorizedMessage } from "../../../../../App/Constants/AbacConstants";
import { useFeaturePermission } from "../../../../../App/State/Hooks/abacHooks";

export default function ReviewHelpful({ review }: { review: ICandidateReview }) {
  const { candidate } = useContext(CandidateContext);
  const user = useCurrentUser();
  const reviewVoteAccess = useFeaturePermission(AppFeatureLookup.ProfileVoteOnCandidateReviews);

  const [helpfulVoteCount, setHelpfulVoteCount] = useState<number>(0);
  const [unHelpfulVoteCount, setUnHelpfulVoteCount] = useState<number>(0);

  const { displayError, displaySuccess } = useNotification();
  const saveHelpfulnessMutation = useSaveReviewHelpfulnessMutation();
  const { isLoading: isVoteLoading } = saveHelpfulnessMutation;

  const existingUserVote = useMemo(() => {
    return review.votes.find((vote) => vote.voterId === user.id);
  }, [review, user]);

  const handleReviewVote: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const button = e.target as HTMLButtonElement;
    const vote = button.dataset.vote ? +button.dataset.vote : undefined;
    if (vote === undefined || vote === existingUserVote?.rating) return;

    saveHelpfulnessMutation.mutate(
      {
        candidateId: candidate.id,
        rating: vote,
        reviewId: review.id,
        voteId: existingUserVote?.id,
        voterId: user.id,
      },
      {
        onError: () => displayError("Error saving your review vote, please try again!"),
        onSuccess: () => {
          displaySuccess("Vote saved successfully!");
        },
      }
    );
  };

  const debouncedReviewHandler = useMemo(
    () => debounce(handleReviewVote, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleReviewVote, candidate, user, review]
  );

  const countVotes = useCallback(() => {
    const helpfulVoteCount = review.votes.filter(
      (vote) => vote.rating === CandidateReviewHelpfulnessType.Helpful
    ).length;
    const unHelpfulVoteCount = review.votes.filter(
      (vote) => vote.rating === CandidateReviewHelpfulnessType.NotHelpful
    ).length;
    setHelpfulVoteCount(helpfulVoteCount);
    setUnHelpfulVoteCount(unHelpfulVoteCount);
  }, [review]);

  useEffect(() => {
    countVotes();
  }, [review.votes, countVotes]);

  return (
    <Box
      id={`review-helpfulVotes-${review.id}`}
      data-test={`review-helpfulVotes-${review.id}`}
      sx={{ color: (theme) => theme.palette.primary.main }}
    >
      <Tooltip title={reviewVoteAccess ? "Vote review as Helpful" : defaultUnauthorizedMessage}>
        <Box sx={{ display: "inline-block" }}>
          <Button
            id={`review-helpfulVote-${review.id}`}
            data-test={`review-helpfulVote-${review.id}`}
            onClick={debouncedReviewHandler}
            data-vote={CandidateReviewHelpfulnessType.Helpful}
            disabled={!reviewVoteAccess || isVoteLoading}
          >
            <ThumbUpOutlined fontSize="small" /> &nbsp; Helpful ({helpfulVoteCount})
          </Button>
        </Box>
      </Tooltip>
      &nbsp;
      <Tooltip title={reviewVoteAccess ? "Vote review as Not Helpful" : defaultUnauthorizedMessage}>
        <Box sx={{ display: "inline-block" }}>
          <Button
            id={`review-notHelpfulVote-${review.id}`}
            data-test={`review-notHelpfulVote-${review.id}`}
            onClick={debouncedReviewHandler}
            data-vote={CandidateReviewHelpfulnessType.NotHelpful}
            disabled={!reviewVoteAccess || isVoteLoading}
          >
            <ThumbDownOutlined fontSize="small" />
            &nbsp; Not Helpful ({unHelpfulVoteCount})
          </Button>
        </Box>
      </Tooltip>
    </Box>
  );
}
